/**
 * 이번 회차 기사 — 구독 중인 고객마다 **그 고객의** 이번 회차 기사를 썼는지 (261007-monthly-article-board).
 *
 * ★ 기사는 회차마다 고객 한 곳에 한 편이다. 회차는 **고객마다** 구독 시작일 기준 한 달이다 — 업체마다 가입일이
 *   달라 한 날짜로 가를 수 없다. 표는 구독 회차 끝이 가까운 순이다.
 * ★ **마감은 그 브랜드의 보고서 회차 끝**이다(`row.deadline`, 운영자 결정 2026-10-12) — 회차는 브랜드의 구독일에서 나와
 *   브랜드마다 다를 수 있다(261015-subscription-unify). 도구줄의 마감은 대상 고객 중 **가장 가까운 것**이다.
 *   기사가 그 회차 산출물·보고서에 잡혀야 해서다.
 * ★ 구독이 없거나·시작 전이거나·끝난 고객은 대상이 아니다 — 아래 "대상 아님"으로 따로 접는다.
 *   구독은 결제 구독 하나이고 **브랜드 상세의 구독 칸**에서 넣고 고친다 — 이 표는 읽고 그리로 보낸다(261015-subscription-unify).
 * ★ 일괄 작성: 미작성 고객을 골라 대기열에 넣는다. 입력은 사람이 고르지 않는다(업종은 업체명, 질문은 가장 오래
 *   안 쓴 목표 질문). 쓰는 것은 서버가 **하나씩** 한다 — 이 화면이 열려 있으면 화면이 "다음 하나"를 계속 부르고
 *   (`useQueueRunner`), 닫으면 전용 스케줄러가 이어서 쓴다.
 * ★ 일괄로 못 쓰는 고객은 체크를 막고 이유를 보인다 — 판단은 서버와 같은 `boardRefusal`.
 */
import { type ArticleBoardRow, boardRefusal } from '@geo-admin/shared/contract/api/article.ts';
import { article } from '@geo-admin/shared/contract/api/index.ts';
import type { BrandKind } from '@geo-admin/shared/contract/common.ts';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import { useApi, useApiMutation } from '../lib/api.ts';
import { call } from '../lib/call.ts';
import { PATHS } from '../lib/paths.ts';
import { cn } from '../lib/utils.ts';
import { ArticleProgressBadge } from './ArticleParts.tsx';
import { Button } from './ui/button.tsx';
import { Badge } from './ui/card.tsx';
import { FieldError } from './ui/form.tsx';
import { ErrorBox, Loading } from './ui/states.tsx';

/** 화면이 동시에 돌리는 작성 수 — 한 편이 1~2분이라 둘이면 30곳이 30분 안팎이다. 더 올리면 속도 제한에 걸린다 */
const RUNNERS = 2;
/** 집을 것이 없거나 오류가 났을 때 다시 묻기까지 */
const IDLE_MS = 15_000;
/** 마감이 이만큼 남았는데 아직 안 썼으면 빨갛게 */
const URGENT_DAYS = 7;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const dot = (d: string | null) => (d ? d.slice(5).replace('-', '.') : '');

/**
 * 대기열이 비지 않은 동안 서버에 "다음 하나"를 되풀이해 묻는다. 화면을 떠나면 멈춘다(스케줄러가 이어 받는다).
 * 한 번에 하나를 쓰고 돌아오므로 요청 하나가 1~2분 걸린다 — 그동안 표는 쓰기 뒤 무효화로 갱신된다.
 */
function useQueueRunner(active: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    const runner = async () => {
      while (!stopped) {
        try {
          const r = await call(article.runArticleQueue, {});
          await qc.invalidateQueries();
          if (!r.ran) {
            if (r.remaining === 0) return;
            await sleep(IDLE_MS);
          }
        } catch {
          await sleep(IDLE_MS);
        }
      }
    };
    for (let i = 0; i < RUNNERS; i++) void runner();
    return () => {
      stopped = true;
    };
  }, [active, qc]);
}

type Phase = 'skipped' | 'running' | 'queued' | 'failed' | 'written' | 'none';

function phaseOf(r: ArticleBoardRow): Phase {
  if (r.article) return 'written';
  if (r.skipped) return 'skipped';
  if (r.job?.status === 'running') return 'running';
  if (r.job?.status === 'queued') return 'queued';
  if (r.job?.status === 'failed') return 'failed';
  return 'none';
}

/**
 * 묶음 — 해야 할 일이 위로 온다(운영자 결정 2026-10-09: "필요한 것만 가시성 좋게").
 * 미작성(실패·대기 포함) → 진행 중(작성 중~승인·대행사 진행) → 게재 완료 → 제외. 같은 묶음 안에서는 마감 순.
 */
type Group = 'todo' | 'progress' | 'done' | 'skipped';
function groupOf(r: ArticleBoardRow): Group {
  if (r.article) return r.article.status === 'published' ? 'done' : 'progress';
  if (r.skipped) return 'skipped';
  return 'todo';
}
const GROUP_ORDER: Record<Group, number> = { todo: 0, progress: 1, done: 2, skipped: 3 };
const FILTERS = [
  ['all', '전체'],
  ['todo', '미작성'],
  ['progress', '진행 중'],
  ['done', '게재 완료'],
] as const;
type Filter = (typeof FILTERS)[number][0];

const INACTIVE_LABEL = { unset: '구독 없음', notStarted: '구독 시작 전', ended: '구독 종료' } as const;

export function ArticleBoard({
  brandKind,
  onWriteManually,
}: {
  /** 「실제 | 테스트」 탭(261023-test-brand) */
  brandKind: BrandKind;
  onWriteManually: (brandId: string) => void;
}) {
  const got = useApi(article.getArticleBoard, { query: { brandKind } }, { refetchInterval: 30_000 });
  const queue = useApiMutation(article.queueArticles);
  const cancel = useApiMutation(article.cancelArticleQueue);
  const skip = useApiMutation(article.skipArticleCycle);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Filter>('all');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = got.data?.board.rows ?? [];
  const open = rows.filter((r) => r.job?.status === 'queued' || r.job?.status === 'running').length;
  useQueueRunner(open > 0);

  if (got.error) return <ErrorBox error={got.error} />;
  if (!got.data) return <Loading />;
  const { writerReady } = got.data.board;
  const deadlineTitle = (d: ArticleBoardRow['deadline']) =>
    `회차별 산출물 ${d.label} 회차 · ${dot(d.from)}~${dot(d.to)}`;

  // 가장 가까운 마감 — 대상 고객 중(구독일이 브랜드마다 달라 마감도 다를 수 있다)
  const nearest = rows
    .filter((r) => r.period.state === 'active')
    .reduce<ArticleBoardRow['deadline'] | null>(
      (m, r) => (m === null || r.deadline.daysLeft < m.daysLeft ? r.deadline : m),
      null,
    );
  const urgent = nearest !== null && nearest.daysLeft <= URGENT_DAYS;

  const active = rows
    .filter((r) => r.period.state === 'active')
    .sort(
      (a, b) =>
        GROUP_ORDER[groupOf(a)] - GROUP_ORDER[groupOf(b)] || (a.period.daysLeft ?? 0) - (b.period.daysLeft ?? 0),
    );
  const inactive = rows.filter((r) => r.period.state !== 'active');
  const counts: Record<Filter, number> = {
    all: active.filter((r) => groupOf(r) !== 'skipped').length,
    todo: active.filter((r) => groupOf(r) === 'todo').length,
    progress: active.filter((r) => groupOf(r) === 'progress').length,
    done: active.filter((r) => groupOf(r) === 'done').length,
  };
  const shown = filter === 'all' ? active : active.filter((r) => groupOf(r) === filter);
  const eligible = shown.filter((r) => boardRefusal(r) === null);
  const chosen = active.filter((r) => picked.has(r.brandId) && boardRefusal(r) === null);
  const inactiveBy = (s: keyof typeof INACTIVE_LABEL) => inactive.filter((r) => r.period.state === s).length;

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allPicked = eligible.length > 0 && eligible.every((r) => picked.has(r.brandId));

  const run = async (job: () => Promise<string | null>) => {
    setError(null);
    setNotice(null);
    try {
      setNotice(await job());
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const onQueue = () =>
    run(async () => {
      if (
        !confirm(
          `${chosen.length}곳의 기사를 씁니다.\n한 편에 1~2분 걸리고 편마다 비용이 듭니다.\n` +
            '이 화면을 열어 두면 바로 이어서 쓰고, 닫으면 서버가 이어서 씁니다.',
        )
      )
        return null;
      const r = await queue.mutateAsync({ body: { brandIds: chosen.map((c) => c.brandId) } });
      setPicked(new Set());
      return r.refused.length > 0
        ? `${r.queued}곳을 대기열에 넣었습니다 · ${r.refused.length}곳은 넣지 못했습니다(${r.refused[0]!.reason})`
        : `${r.queued}곳을 대기열에 넣었습니다`;
    });

  const onCancel = () =>
    run(async () => {
      if (!confirm('기다리는 작성을 모두 거둡니다. 지금 쓰는 중인 기사는 끝까지 씁니다.')) return null;
      await cancel.mutateAsync({});
      return '대기를 취소했습니다';
    });

  const onSkip = (r: ArticleBoardRow, skipped: boolean) =>
    run(async () => {
      await skip.mutateAsync({ body: { brandId: r.brandId, skipped } });
      return skipped ? `${r.brandName}을(를) 이번 회차에서 뺐습니다` : `${r.brandName}을(를) 다시 대상에 넣었습니다`;
    });

  const action = 'text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline';
  /** 구독은 브랜드 상세의 구독 칸에서 넣고 고친다 — 고칠 곳이 한 곳뿐이다(261015-subscription-unify) */
  const subscriptionLink = (r: ArticleBoardRow, label: string) => (
    <Link to={PATHS.admin.brand} params={{ id: r.brandId }} className={action}>
      {label}
    </Link>
  );

  // 카드 껍데기는 부르는 쪽(기사 작성 화면의 탭)이 둔다 — 여기는 표와 그 도구줄만
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {nearest ? (
            <span className="mr-2 text-slate-600 text-sm" title={`가장 가까운 마감 · ${deadlineTitle(nearest)}`}>
              마감 <span className="font-medium tabular-nums">{dot(nearest.to)}</span>{' '}
              <Badge tone={urgent && counts.todo > 0 ? 'red' : 'slate'}>
                {nearest.daysLeft === 0 ? '오늘' : `D-${nearest.daysLeft}`}
              </Badge>
            </span>
          ) : null}
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={cn(
                'rounded-full px-3 py-1 text-sm transition-colors',
                filter === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                key === 'todo' && counts.todo > 0 && filter !== key && 'bg-red-50 text-red-700 hover:bg-red-100',
              )}
            >
              {label} <span className="tabular-nums opacity-70">{counts[key]}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {open > 0 ? (
            <>
              <span className="text-slate-500 text-xs">일괄 작성 중 · 남은 {open}편</span>
              <Button size="sm" variant="outline" onClick={() => void onCancel()} disabled={cancel.isPending}>
                대기 취소
              </Button>
            </>
          ) : null}
          <Button
            size="sm"
            onClick={() => void onQueue()}
            disabled={chosen.length === 0 || queue.isPending || !writerReady}
            title={writerReady ? '미작성 고객을 골라 한꺼번에 씁니다' : '기사 생성 키가 설정되지 않았습니다'}
          >
            {queue.isPending ? '넣는 중…' : chosen.length > 0 ? `${chosen.length}곳 일괄 작성` : '일괄 작성'}
          </Button>
        </div>
      </div>
      {notice ? <p className="text-emerald-700 text-sm">{notice}</p> : null}
      <FieldError>{error}</FieldError>

      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col className="w-8" />
          <col className="w-44" />
          <col className="w-28" />
          <col className="w-32" />
          <col />
          <col className="w-44" />
        </colgroup>
        <thead className="border-slate-200 border-b text-left text-slate-400 text-xs">
          <tr>
            <th className="py-2">
              <input
                type="checkbox"
                aria-label="일괄로 쓸 수 있는 고객 모두 고르기"
                checked={allPicked}
                disabled={eligible.length === 0}
                onChange={() =>
                  setPicked((prev) => {
                    const next = new Set(prev);
                    for (const r of eligible) {
                      if (allPicked) next.delete(r.brandId);
                      else next.add(r.brandId);
                    }
                    return next;
                  })
                }
              />
            </th>
            <th className="py-2 font-medium">고객</th>
            <th className="py-2 font-medium">마감</th>
            <th className="py-2 font-medium">상태</th>
            <th className="py-2 font-medium">기사</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => {
            const refusal = boardRefusal(r);
            const phase = phaseOf(r);
            const group = groupOf(r);
            const late = group === 'todo' && r.deadline.daysLeft <= URGENT_DAYS;
            const writable = group === 'todo' && phase !== 'queued' && phase !== 'running';
            return (
              <Fragment key={r.brandId}>
                <tr
                  className={cn(
                    'group border-slate-100 border-b hover:bg-slate-50',
                    group === 'skipped' && 'opacity-50',
                  )}
                >
                  <td className="py-2.5">
                    <input
                      type="checkbox"
                      aria-label={`${r.brandName} 고르기`}
                      checked={picked.has(r.brandId) && refusal === null}
                      disabled={refusal !== null}
                      title={refusal ?? undefined}
                      onChange={() => toggle(r.brandId)}
                    />
                  </td>
                  <td className="truncate py-2.5 pr-3 font-medium text-slate-900" title={r.brandName}>
                    {r.brandName}
                  </td>
                  <td
                    className="py-2.5 pr-3"
                    title={`${deadlineTitle(r.deadline)} · ${(r.period.index ?? 0) >= 1 ? `구독 ${r.period.index}회차` : '구독 시작 전 회차'}`}
                  >
                    <Badge tone={late ? 'red' : 'slate'}>
                      {r.deadline.daysLeft === 0 ? '오늘 마감' : `D-${r.deadline.daysLeft}`}
                    </Badge>{' '}
                    <span className="text-slate-400 text-xs tabular-nums">{dot(r.deadline.to)}</span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <StatusBadge row={r} phase={phase} />
                  </td>
                  <td className="truncate py-2.5 pr-3">
                    {r.article ? (
                      <Link
                        to="/admin/articles/$id"
                        params={{ id: r.article.id }}
                        className="text-slate-700 hover:text-slate-900 hover:underline"
                        title={r.article.title}
                      >
                        {r.article.title}
                      </Link>
                    ) : phase === 'failed' ? (
                      <span className="text-red-600 text-xs" title={r.job?.error ?? undefined}>
                        {r.job?.error} — 다시 골라 일괄 작성
                      </span>
                    ) : group === 'todo' && refusal && writable ? (
                      <span className="text-amber-700 text-xs" title={refusal}>
                        {refusal}
                      </span>
                    ) : group === 'todo' ? (
                      <span
                        className="text-slate-400 text-xs"
                        title={`${r.industry} · ${r.representativeName} · 질문: ${r.nextQuery}`}
                      >
                        질문 · {r.nextQuery}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2.5 text-right text-xs">
                    <span
                      className={cn(
                        'inline-flex gap-3 whitespace-nowrap',
                        group !== 'skipped' && 'opacity-0 focus-within:opacity-100 group-hover:opacity-100',
                      )}
                    >
                      {writable ? (
                        <button type="button" className={action} onClick={() => onWriteManually(r.brandId)}>
                          직접 쓰기
                        </button>
                      ) : null}
                      {group === 'skipped' ? (
                        <button type="button" className={action} onClick={() => void onSkip(r, false)}>
                          되돌리기
                        </button>
                      ) : writable ? (
                        <button type="button" className={action} onClick={() => void onSkip(r, true)}>
                          제외
                        </button>
                      ) : null}
                      {subscriptionLink(r, '구독')}
                    </span>
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {shown.length === 0 ? (
        <p className="py-6 text-center text-slate-500 text-sm">
          {active.length === 0
            ? '구독 중인 고객이 없습니다 — 아래 「구독 등록」에서 브랜드 구독을 넣어 주세요'
            : '해당하는 고객이 없습니다'}
        </p>
      ) : null}

      {inactive.length > 0 ? (
        <details className="text-sm">
          <summary className="cursor-pointer select-none text-slate-500">
            대상 아님 {inactive.length}곳 · 구독 없음 {inactiveBy('unset')} · 시작 전 {inactiveBy('notStarted')} · 종료{' '}
            {inactiveBy('ended')}
          </summary>
          <table className="mt-2 w-full table-fixed text-sm">
            <colgroup>
              <col className="w-8" />
              <col className="w-44" />
              <col className="w-28" />
              <col className="w-32" />
              <col />
              <col className="w-44" />
            </colgroup>
            <tbody>
              {inactive.map((r) => (
                <Fragment key={r.brandId}>
                  <tr className="group border-slate-100 border-b">
                    <td />
                    <td className="truncate py-2 pr-3 text-slate-700">{r.brandName}</td>
                    <td colSpan={2} className="py-2 pr-3">
                      <Badge tone={r.period.state === 'unset' ? 'amber' : 'slate'}>
                        {INACTIVE_LABEL[r.period.state as keyof typeof INACTIVE_LABEL]}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-slate-400 text-xs">
                      {r.subscription
                        ? `${dot(r.subscription.startDate)} 시작${r.subscription.endDate ? ` · ${dot(r.subscription.endDate)} 종료` : ''}`
                        : null}
                    </td>
                    <td className="py-2 text-right text-xs">
                      {subscriptionLink(r, r.period.state === 'unset' ? '구독 등록' : '구독')}
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}

/** 상태 — 배지 하나. 실패 이유·대기 같은 자세한 것은 배지에 마우스를 올리면 보인다 */
function StatusBadge({ row, phase }: { row: ArticleBoardRow; phase: Phase }) {
  switch (phase) {
    case 'written':
      return <ArticleProgressBadge status={row.article!.status} pressStatus={row.article!.pressStatus} />;
    case 'skipped':
      return <Badge tone="slate">이번 회차 제외</Badge>;
    case 'running':
      return <Badge tone="amber">작성 중…</Badge>;
    case 'queued':
      return <Badge tone="slate">작성 대기</Badge>;
    case 'failed':
      return (
        <Badge tone="red" title={row.job?.error ?? undefined}>
          작성 실패
        </Badge>
      );
    default:
      return <Badge tone="red">미작성</Badge>;
  }
}
